import React, { useMemo, useState } from "react";
import { SB, KEY } from "../api/supabase";
import "./FeedbackUpdates.css";

const STATUS = {
  new: "접수 완료",
  reviewing: "검토 중",
  planned: "반영 예정",
  in_progress: "개선 중",
  completed: "개선 완료",
  held: "보류",
};
const TYPES = { bug: "오류", inconvenience: "불편함", suggestion: "기능 제안", question: "사용 방법 문의" };
const dateLabel = (value) => value ? new Date(value).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" }) : "";

export default function FeedbackUpdates({ user, surface = "buyer" }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const visibleItems = useMemo(() => items.filter((item) => item.audience === "all" || item.audience === surface), [items, surface]);
  async function load() {
    if (!user?.token || !user?.id) { setError("로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요."); return; }
    setLoading(true); setError("");
    try {
      const updatesQuery = new URLSearchParams({ select: "id,title,summary,planned_action,completion_note,status,audience,published_at,updated_at", is_published: "eq.true", order: "published_at.desc", limit: "100" });
      const mineQuery = new URLSearchParams({ select: "id,feedback_type,message,status,admin_note,created_at,updated_at", user_id: `eq.${user.id}`, order: "created_at.desc", limit: "100" });
      const headers = { apikey: KEY, Authorization: `Bearer ${user.token}` };
      const [updatesResponse, mineResponse] = await Promise.all([
        fetch(`${SB}/rest/v1/feedback_updates?${updatesQuery}`, { headers }),
        fetch(`${SB}/rest/v1/beta_feedback?${mineQuery}`, { headers }),
      ]);
      const [updatesData, mineData] = await Promise.all([updatesResponse.json().catch(() => null), mineResponse.json().catch(() => null)]);
      if (!updatesResponse.ok) throw new Error(updatesData?.message || updatesData?.error || "개선 현황을 불러오지 못했습니다.");
      if (!mineResponse.ok) throw new Error(mineData?.message || mineData?.error || "내 의견을 불러오지 못했습니다.");
      setItems(Array.isArray(updatesData) ? updatesData : []);
      setMine(Array.isArray(mineData) ? mineData : []);
    } catch (loadError) { setError(loadError?.message || "의견 목록을 불러오지 못했습니다."); }
    finally { setLoading(false); }
  }
  function show() { setOpen(true); setTab("all"); setExpandedId(""); load(); }
  const currentItems = tab === "all" ? visibleItems : mine;
  return (<>
    <button className="feedback-updates-launcher" type="button" onClick={show} aria-label="의견 보기" title="의견 보기"><span>의견 보기</span></button>
    {open && <div className="feedback-updates-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
      <section className="feedback-updates-dialog" role="dialog" aria-modal="true" aria-labelledby="feedback-updates-title" onMouseDown={(event) => event.stopPropagation()}>
        <header><div><strong id="feedback-updates-title">D-Works 의견</strong><p>전체 개선 현황과 내가 보낸 의견을 확인합니다.</p></div><button type="button" onClick={() => setOpen(false)} aria-label="닫기">×</button></header>
        <div className="feedback-updates-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === "all"} className={tab === "all" ? "active" : ""} onClick={() => { setTab("all"); setExpandedId(""); }}>전체 의견</button>
          <button type="button" role="tab" aria-selected={tab === "mine"} className={tab === "mine" ? "active" : ""} onClick={() => { setTab("mine"); setExpandedId(""); }}>내 의견</button>
        </div>
        {loading && <p className="feedback-updates-state" role="status">의견 목록을 불러오는 중입니다.</p>}
        {!loading && error && <div className="feedback-updates-error" role="alert"><span>{error}</span><button type="button" onClick={load}>다시 시도</button></div>}
        {!loading && !error && !currentItems.length && <p className="feedback-updates-state">{tab === "all" ? "공개된 개선 현황이 아직 없습니다." : "보낸 의견이 아직 없습니다."}</p>}
        {!loading && !error && !!currentItems.length && <div className="feedback-updates-list">
          {currentItems.map((item) => <article key={item.id} className={expandedId === item.id ? "expanded" : ""}>
            <button className="feedback-updates-summary" type="button" onClick={() => setExpandedId(current => current === item.id ? "" : item.id)} aria-expanded={expandedId === item.id} aria-controls={`feedback-update-${item.id}`}>
              <h3>{tab === "all" ? item.title : TYPES[item.feedback_type] || "의견"}</h3><span aria-hidden="true">⌄</span>
            </button>
            {expandedId === item.id && <div className="feedback-updates-detail" id={`feedback-update-${item.id}`}>
              <div className="feedback-updates-meta"><span className={`status-${item.status}`}>{STATUS[item.status] || item.status}</span><time>{dateLabel(tab === "all" ? item.published_at || item.updated_at : item.created_at)}</time></div>
              {tab === "mine" ? <>
                <section><b>내가 보낸 의견</b><p>{item.message}</p></section>
                {item.admin_note && <section className="completed"><b>D-Works 답변</b><p>{item.admin_note}</p></section>}
              </> : <>
                <section><b>접수된 의견</b><p>{item.summary}</p></section>
                {item.planned_action && <section><b>개선 계획</b><p>{item.planned_action}</p></section>}
                {item.completion_note && <section className="completed"><b>완료 결과</b><p>{item.completion_note}</p></section>}
              </>}
            </div>}
          </article>)}
        </div>}
      </section>
    </div>}
  </>);
}
